import {
  formatMinor,
  PULSE_TIER_ORDER,
  type ClientSummary,
  type Organization,
  type PulseTier,
} from '@hvac/contracts';
import { Card, CardBody, cn, PageHeader, Skeleton } from '@hvac/ui';
import { useQueries, useSuspenseQuery } from '@tanstack/react-query';
import { inboxSummaryQuery } from '../../shared/api/document-queries.js';
import { clientSummaryQuery } from '../../shared/api/firm-queries.js';
import { pulseQuery } from '../../shared/api/pulse-queries.js';
import { sessionQuery, setActiveOrganization } from '../../shared/api/queries.js';

/**
 * Mission Control, the firm's morning.
 *
 * One row per client: the month's state (what is waiting, what is ready to
 * sign, when the bank was last seen, the last close, the next HST return and
 * how long until it is due, what is owed both ways), what their Pulse would
 * say, and one click to be inside their books. The lateness is sorted to the
 * top. Aggregation without a new privilege: every number here comes from the
 * same per-client endpoints the client's own pages use, each authorized
 * separately against that client's membership.
 */

const TIER_LABELS: Record<PulseTier, string> = {
  urgent: 'urgent',
  costing: 'costing money',
  opportunity: 'opportunities',
  fyi: 'FYI',
};

const TIER_DOTS: Record<PulseTier, string> = {
  urgent: 'bg-danger',
  costing: 'bg-warning',
  opportunity: 'bg-brand-500',
  fyi: 'bg-content-subtle',
};

const DAY = 24 * 60 * 60 * 1000;
const STATEMENT_STALE_DAYS = 45;
const HST_SOON_DAYS = 14;

function daysBetween(fromIso: string, to: Date): number {
  return Math.floor((to.getTime() - new Date(`${fromIso}T00:00:00Z`).getTime()) / DAY);
}

interface Cell {
  label: string;
  value: string;
  tone: 'ok' | 'warn' | 'late' | 'quiet';
}

/** The month as six short cells, each a plain statement, worst tone wins the row. */
export function describeClient(
  summary: ClientSummary,
  today: Date,
): { cells: Cell[]; urgency: number } {
  const cells: Cell[] = [];
  let urgency = 0;

  if (summary.documentsWaiting === 0) {
    cells.push({ label: 'Waiting', value: 'Nothing', tone: 'ok' });
  } else {
    const age = summary.oldestWaiting === null ? 0 : daysBetween(summary.oldestWaiting, today);
    const late = age > 14;
    urgency = Math.max(urgency, late ? 2 : 1);
    cells.push({
      label: 'Waiting',
      value: `${String(summary.documentsWaiting)} document${summary.documentsWaiting === 1 ? '' : 's'}${age > 0 ? `, oldest ${String(age)}d` : ''}`,
      tone: late ? 'late' : 'warn',
    });
  }

  cells.push(
    summary.readyToSign === 0
      ? {
          label: 'To sign',
          value:
            summary.drafts === 0
              ? 'Nothing'
              : `${String(summary.drafts)} draft${summary.drafts === 1 ? '' : 's'} still need decisions`,
          tone: summary.drafts === 0 ? 'ok' : 'warn',
        }
      : { label: 'To sign', value: `${String(summary.readyToSign)} ready`, tone: 'warn' },
  );

  if (summary.lastStatementOn === null) {
    urgency = Math.max(urgency, 1);
    cells.push({ label: 'Bank', value: 'No statement yet', tone: 'warn' });
  } else {
    const age = daysBetween(summary.lastStatementOn, today);
    const stale = age > STATEMENT_STALE_DAYS;
    if (stale) urgency = Math.max(urgency, 2);
    cells.push({
      label: 'Bank',
      value: `Seen to ${summary.lastStatementOn}${summary.unmatchedBankLines > 0 ? `, ${String(summary.unmatchedBankLines)} unmatched` : ''}`,
      tone: stale ? 'late' : summary.unmatchedBankLines > 0 ? 'warn' : 'ok',
    });
  }

  cells.push(
    summary.lastClosedLabel === null
      ? { label: 'Closed through', value: 'Never', tone: 'quiet' }
      : { label: 'Closed through', value: summary.lastClosedLabel, tone: 'ok' },
  );

  if (summary.hstFilingDueOn === null) {
    cells.push({
      label: 'HST',
      value: summary.hstPeriodLabel === null ? 'Filing periods not set' : 'No due date',
      tone: 'quiet',
    });
  } else {
    const left = -daysBetween(summary.hstFilingDueOn, today);
    const tone: Cell['tone'] = left < 0 ? 'late' : left <= HST_SOON_DAYS ? 'warn' : 'ok';
    if (left < 0) urgency = Math.max(urgency, 3);
    else if (left <= HST_SOON_DAYS) urgency = Math.max(urgency, 2);
    cells.push({
      label: 'HST',
      value:
        left < 0
          ? `${String(-left)} day${left === -1 ? '' : 's'} overdue (${summary.hstPeriodLabel ?? ''})`
          : left === 0
            ? 'Due today'
            : `Due in ${String(left)} day${left === 1 ? '' : 's'}`,
      tone,
    });
  }

  const owed = BigInt(summary.billsOverdueMinor);
  const owedToUs = BigInt(summary.owedToUsMinor);
  if (summary.billsOverdue > 0) urgency = Math.max(urgency, 1);
  cells.push({
    label: 'Money',
    value:
      summary.billsOverdue === 0 && owedToUs === 0n
        ? 'Nothing owed either way'
        : [
            summary.billsOverdue > 0 ? `owes ${formatMinor(owed.toString())} past due` : null,
            owedToUs > 0n ? `is owed ${formatMinor(owedToUs.toString())}` : null,
          ]
            .filter((part) => part !== null)
            .join(', '),
    tone: summary.billsOverdue > 0 ? 'warn' : 'ok',
  });

  return { cells, urgency };
}

const TONE_CLASSES: Record<Cell['tone'], string> = {
  ok: 'text-content',
  warn: 'text-warning',
  late: 'text-danger font-medium',
  quiet: 'text-content-muted',
};

export function MissionControlPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizations = session.organizations.map((entry) => entry.organization);
  const today = new Date();

  const pulses = useQueries({
    queries: organizations.map((organization) => ({
      ...pulseQuery(organization.id),
      staleTime: 60_000,
    })),
  });
  const inboxes = useQueries({
    queries: organizations.map((organization) => ({
      ...inboxSummaryQuery(organization.id),
      staleTime: 60_000,
    })),
  });
  const summaries = useQueries({
    queries: organizations.map((organization) => clientSummaryQuery(organization.id)),
  });

  const open = (organizationId: string): void => {
    setActiveOrganization(organizationId);
    window.location.assign('/');
  };

  const rows = organizations
    .map((organization, index) => {
      const summary = summaries[index]?.data;
      const described = summary === undefined ? null : describeClient(summary, today);
      return { organization, index, described };
    })
    .sort((a, b) => (b.described?.urgency ?? 0) - (a.described?.urgency ?? 0));

  const late = rows.filter((row) => (row.described?.urgency ?? 0) >= 2).length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Firm"
        title="Clients"
        description={
          organizations.length === 0
            ? 'No businesses yet. Create one, or accept an invitation to one.'
            : late === 0
              ? `${String(organizations.length)} business${organizations.length === 1 ? '' : 'es'}, none of them late. The ones with the most waiting are first.`
              : `${String(late)} of ${String(organizations.length)} business${organizations.length === 1 ? '' : 'es'} ${late === 1 ? 'is' : 'are'} late on something. They are first.`
        }
      />

      <div className="stagger-rise flex flex-col gap-4">
        {rows.map(({ organization, index, described }) => (
          <ClientRow
            key={organization.id}
            organization={organization}
            cells={described?.cells ?? null}
            pulseCards={pulses[index]?.data?.cards ?? null}
            pulsePending={pulses[index]?.isPending === true}
            inboxWaiting={(() => {
              const needs = inboxes[index]?.data?.needsYou;
              return needs === undefined
                ? 0
                : needs.review + needs.statements + needs.proposals + needs.unknown;
            })()}
            onOpen={() => {
              open(organization.id);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ClientRow({
  organization,
  cells,
  pulseCards,
  pulsePending,
  inboxWaiting,
  onOpen,
}: {
  organization: Organization;
  cells: Cell[] | null;
  pulseCards: readonly { tier: PulseTier }[] | null;
  pulsePending: boolean;
  inboxWaiting: number;
  onOpen: () => void;
}) {
  const tierCounts = PULSE_TIER_ORDER.map((tier) => ({
    tier,
    count: (pulseCards ?? []).filter((card) => card.tier === tier).length,
  })).filter((entry) => entry.count > 0);

  return (
    <Card className="hover-lift">
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button type="button" onClick={onOpen} className="min-w-0 text-left">
            <p className="truncate text-lg font-semibold text-content hover:text-brand-700">
              {organization.name}
            </p>
            <p className="mt-0.5 text-sm text-content-muted">
              {pulsePending ? (
                'Reading their books…'
              ) : tierCounts.length === 0 && inboxWaiting === 0 ? (
                'A quiet pulse.'
              ) : (
                <>
                  {tierCounts.length === 0
                    ? 'A quiet pulse'
                    : tierCounts
                        .map((entry) => `${String(entry.count)} ${TIER_LABELS[entry.tier]}`)
                        .join(', ')}
                  {inboxWaiting > 0
                    ? ` · ${String(inboxWaiting)} item${inboxWaiting === 1 ? '' : 's'} waiting in intake`
                    : ''}
                </>
              )}
            </p>
          </button>

          <div className="flex items-center gap-3">
            {pulsePending ? (
              <Skeleton className="h-5 w-24" />
            ) : (
              <div className="flex items-center gap-2">
                {tierCounts.map((entry) => (
                  <span
                    key={entry.tier}
                    className="flex items-center gap-1.5 text-sm text-content-muted"
                    title={TIER_LABELS[entry.tier]}
                  >
                    <span
                      className={cn('inline-block size-2 rounded-full', TIER_DOTS[entry.tier])}
                    />
                    <span className="tabular font-medium text-content">{entry.count}</span>
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={onOpen}
              className={cn(
                'rounded-[var(--radius-control)] border border-border px-4 py-2 text-sm font-medium',
                'text-content transition-all duration-[var(--duration-quick)]',
                'hover:border-brand-300 hover:text-brand-700 hover:shadow-[var(--shadow-glow)]',
              )}
            >
              Open →
            </button>
          </div>
        </div>

        {cells === null ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-3 text-sm sm:grid-cols-3 lg:grid-cols-6">
            {cells.map((cell) => (
              <div key={cell.label} className="min-w-0">
                <dt className="text-xs text-content-muted">{cell.label}</dt>
                <dd className={cn('truncate', TONE_CLASSES[cell.tone])} title={cell.value}>
                  {cell.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </CardBody>
    </Card>
  );
}
